// Session state for the admin app: who is signed in and what they may do.
//
// Permissions come from the server on every load rather than from the token,
// so revoking a role takes effect on the next refresh. `can()` is the single
// place the UI asks about a permission — it mirrors the backend check, but it
// is only ever a convenience for hiding controls. The backend is the authority;
// hiding a button is not access control.

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from 'react';
import {
    fetchCurrentUser,
    getToken,
    login as apiLogin,
    logout as apiLogout,
    setUnauthorizedHandler,
    type CurrentUser,
} from './api';

type AuthState = {
    /** Null until the initial token check finishes, to avoid flashing the login screen. */
    loading: boolean;
    user: CurrentUser['user'] | null;
    access: CurrentUser['access'] | null;
    signIn: (username: string, password: string) => Promise<void>;
    signOut: () => void;
    /** Does the signed-in user hold this permission anywhere? */
    can: (permission: string) => boolean;
    /** Can they see this project? */
    canSeeProject: (projectId: string) => boolean;
    /** Re-read identity and permissions, e.g. after editing your own roles. */
    refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [loading, setLoading] = useState(true);
    const [current, setCurrent] = useState<CurrentUser | null>(null);

    const load = useCallback(async () => {
        if (!getToken()) {
            setCurrent(null);
            setLoading(false);
            return;
        }
        try {
            setCurrent(await fetchCurrentUser());
        } catch {
            // Token present but rejected (expired, or the account was disabled).
            apiLogout();
            setCurrent(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Any 401 from anywhere in the app clears the session, which re-renders
    // this provider's children as the login screen.
    useEffect(() => {
        setUnauthorizedHandler(() => setCurrent(null));
        return () => setUnauthorizedHandler(null);
    }, []);

    const signIn = useCallback(async (username: string, password: string) => {
        await apiLogin(username, password);
        setCurrent(await fetchCurrentUser());
    }, []);

    const signOut = useCallback(() => {
        apiLogout();
        setCurrent(null);
    }, []);

    const value = useMemo<AuthState>(() => {
        const permissions = new Set(current?.access.permissions ?? []);
        const projectIds = new Set(current?.access.projectIds ?? []);
        const allProjects = current?.access.allProjects ?? false;

        return {
            loading,
            user: current?.user ?? null,
            access: current?.access ?? null,
            signIn,
            signOut,
            can: (permission) => permissions.has(permission),
            canSeeProject: (projectId) => allProjects || projectIds.has(String(projectId)),
            refresh: load,
        };
    }, [current, loading, signIn, signOut, load]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
    return ctx;
}
